import React from 'react';

import styled from '@emotion/styled';

import {
  Box,
  COLORS,
  FontWeight,
  SkeletonLoader,
  Text,
  mq,
} from '@clutter/clean';

import { RateGroupKindEnum } from '@graphql/platform';

import {
  COMMITMENT_TO_FREE_SUBSEQUENT_JOBS,
  COMMITMENT_TO_LENGTH,
  plan,
} from '@root/constants/pricing';
import { Commitment } from '@root/resources/types/commitment';
import { PlanName } from '@root/resources/types/plan_name';

import { Currency } from '@shared/currency';
import { Fallback } from '@shared/fallback';

import { PricingSummary } from '@utils/hooks/pricing/use_pricing_summary';

import twinkleStars from '@images/illustrations/twinkle_stars.svg';
import twinkleStarsMobile from '@images/illustrations/twinkle_stars_small.svg';

const PriceText = styled(Text.Body)`
  ${mq({
    whiteSpace: ['normal', null, null, 'nowrap'],
  })}
`;

interface BannerProps {
  planSize?: PlanName;
  commitment?: Commitment;
  pricingSummary?: PricingSummary;
}

export const Banner: React.FC<BannerProps> = ({
  planSize,
  commitment,
  pricingSummary,
}) => {
  const commitmentData = commitment
    ? {
        commitmentLength: COMMITMENT_TO_LENGTH[commitment],
        freeSubsequentJobs: COMMITMENT_TO_FREE_SUBSEQUENT_JOBS[commitment],
        article: COMMITMENT_TO_LENGTH[commitment] === 8 ? 'An' : 'A',
      }
    : {};
  return (
    <Box
      position="relative"
      margin="24px 0 0"
      background="#EFEFEF"
      color={COLORS.tealDark}
      borderRadius="4px"
      border={`1px solid ${COLORS.grayBorder}`}
    >
      <Box
        position="absolute"
        bottom="0"
        left="4px"
        display={['none', null, 'block']}
        style={{ pointerEvents: 'none' }}
      >
        <img
          src={twinkleStars}
          alt=""
          style={{ display: 'block', margin: 0, padding: 0 }}
          draggable={false}
        />
      </Box>
      <Box
        position="absolute"
        bottom="0"
        right="0"
        display={['block', null, 'none']}
        style={{ pointerEvents: 'none' }}
      >
        <img
          src={twinkleStarsMobile}
          alt=""
          style={{ display: 'block', margin: 0, padding: 0 }}
          draggable={false}
        />
      </Box>
      <Box
        padding={['12px 24px', null, '18px 18px 18px 80px']}
        position="relative"
      >
        <Box.Flex justifyContent="space-between" gap="24px">
          <Box.FlexItem flexGrow={1}>
            <Box margin="0 0 8px">
              <Text.Body weight={FontWeight.Medium}>What you’ll pay</Text.Body>
            </Box>
            <Text.Callout>
              {planSize && <>{plan(planSize).label} Unit. </>}
              {commitment === RateGroupKindEnum.Mover ? (
                <>
                  No monthly minimum. Includes: Free initial pickup & final
                  delivery. Purchase more pickups and deliveries anytime.
                </>
              ) : (
                <>
                  {commitment && (
                    <>
                      {commitmentData.article} {commitmentData.commitmentLength}{' '}
                      month minimum. Includes: Free initial pickup & final
                      delivery, plus{' '}
                      <b>{commitmentData.freeSubsequentJobs} additional free</b>{' '}
                      pickups & deliveries.
                    </>
                  )}
                </>
              )}
            </Text.Callout>
          </Box.FlexItem>
          <Box.FlexItem>
            {pricingSummary && (
              <Fallback
                delayMs={0}
                placeholder={
                  <Box.Flex height="32px" width="80px" flexShrink={0} />
                }
                loader={
                  <SkeletonLoader height="32px" width="80px" flexShrink={0} />
                }
                value={pricingSummary?.monthlyAmount !== undefined}
              >
                <Box>
                  <Box
                    padding="2px 4px"
                    background={COLORS.tealBackground}
                    borderRadius="4px"
                    textAlign="right"
                  >
                    <PriceText weight={FontWeight.Medium}>
                      <Currency amount={pricingSummary?.monthlyAmount} />
                      /mo{' '}
                      {!!pricingSummary?.onboardingFeeAmount && (
                        <>
                          +&nbsp;
                          <Currency
                            amount={pricingSummary?.onboardingFeeAmount}
                          />
                          &nbsp;fee
                        </>
                      )}
                    </PriceText>
                  </Box>
                </Box>
              </Fallback>
            )}
          </Box.FlexItem>
        </Box.Flex>
      </Box>
    </Box>
  );
};
